export const industries = [
  {
    id: "real-estate",
    name: "Real Estate",
    path: "/industries/real-estate",
    number: "01",
    heroLabel: "Industry Solutions",
    heroTitle: "Digital systems and websites for real estate teams that need trust and speed.",
    heroDescription:
      "We help real estate businesses present listings clearly, capture inquiries faster, and support agents with workflows that are easier to manage day to day.",
    heroImageUrl: "https://images.pexels.com/photos/10752001/pexels-photo-10752001.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    heroImageAlt: "A modern interior with sunlight streaming through geometric patterns and glass details.",
    heroImageCredit: "Brett Sayles",
    heroImageLink: "https://www.pexels.com/photo/balconies-in-building-10752001/",
    overviewTitle: "Real estate experiences built around credibility, listing clarity, and lead response.",
    overviewParagraphs: [
      "Real estate websites and internal workflows need to move quickly without feeling chaotic. Buyers, sellers, and investors make decisions based on trust, clarity, and how easy it is to take the next step.",
      "We help real estate teams improve website presentation, listing organization, lead capture, booking flows, and supporting systems so the business feels more polished and easier to operate."
    ],
    bestFor: "Brokerages, solo agents, property groups, developers, and real estate marketing teams.",
    focus: "Listing presentation, inquiry flow, lead routing, scheduling, and brand credibility.",
    process: [
      {
        step: "Step 01",
        title: "Business and audience review",
        description: "We look at the kinds of clients you serve, how leads arrive, and what information prospects need before they contact you."
      },
      {
        step: "Step 02",
        title: "Experience planning",
        description: "We shape the page structure, listing flow, and lead capture path so prospects can move with less friction."
      },
      {
        step: "Step 03",
        title: "Design and system alignment",
        description: "We create a cleaner visual system and organize the supporting workflow around your real sales process."
      },
      {
        step: "Step 04",
        title: "Launch support",
        description: "We prepare the project so your team can publish, manage, and follow up on inquiries with more confidence."
      }
    ],
    outcomes:
      "Stronger first impressions, clearer listing presentation, faster lead handling, and a more organized digital workflow.",
    highlights: [
      "Property and listing landing pages",
      "Inquiry and viewing request flows",
      "Brokerage websites and brand systems",
      "Lead dashboards and client follow-up support"
    ]
  },
  {
    id: "resorts",
    name: "Resorts",
    path: "/industries/resorts",
    number: "02",
    heroLabel: "Industry Solutions",
    heroTitle: "Resort websites and booking experiences designed to sell the stay before the guest ever arrives.",
    heroDescription:
      "We help resorts present their rooms, amenities, experiences, and atmosphere more clearly while supporting direct inquiries, bookings, and stronger visual trust.",
    heroImageUrl: "https://images.pexels.com/photos/261102/pexels-photo-261102.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    heroImageAlt: "Luxury beach resort pool with palm trees and ocean-facing lounge area.",
    heroImageCredit: "Pixabay",
    heroImageLink: "https://www.pexels.com/photo/swimming-pool-near-palm-trees-261102/",
    overviewTitle: "Resort digital experiences built for atmosphere, direct bookings, and premium guest trust.",
    overviewParagraphs: [
      "A resort website has to do more than list rooms and rates. It needs to communicate the feeling of the place, the quality of the stay, and why the guest should choose you over other destinations.",
      "We help resorts shape room pages, gallery-driven landing pages, booking prompts, and experience-led storytelling so guests can picture the stay clearly and act with more confidence."
    ],
    bestFor: "Beach resorts, boutique stays, villas, island properties, nature resorts, and hospitality groups.",
    focus: "Direct booking support, visual storytelling, room presentation, and experience-driven conversion flow.",
    process: [
      {
        step: "Step 01",
        title: "Guest journey review",
        description: "We identify what travelers need to see first, what builds desire, and where the current site loses momentum."
      },
      {
        step: "Step 02",
        title: "Stay and amenity planning",
        description: "We organize rooms, amenities, activities, dining, and booking pathways so the stay feels more complete and easier to understand."
      },
      {
        step: "Step 03",
        title: "Visual experience refinement",
        description: "We shape the design around premium photography, clearer hierarchy, and stronger calls to action for bookings or inquiries."
      },
      {
        step: "Step 04",
        title: "Booking-flow alignment",
        description: "We connect the website experience to your inquiry or booking process so the handoff feels smoother for both guests and staff."
      }
    ],
    outcomes:
      "Stronger first impressions, better room and amenity presentation, more confident inquiries, and improved direct-booking readiness.",
    highlights: [
      "Resort and villa websites",
      "Room and amenity landing pages",
      "Direct booking and inquiry flows",
      "Gallery-led hospitality storytelling"
    ]
  },
  {
    id: "restaurants",
    name: "Restaurants",
    path: "/industries/restaurants",
    number: "03",
    heroLabel: "Industry Solutions",
    heroTitle: "Restaurant websites and campaigns that make menus, bookings, and promotions easier to act on.",
    heroDescription:
      "We help restaurants present their food, brand, and service more clearly online while supporting reservations, promotions, and local discovery.",
    heroImageUrl: "https://images.pexels.com/photos/7627408/pexels-photo-7627408.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    heroImageAlt: "Various appetizing dishes served on white plates and glasses of wine on wooden table in modern light restaurant.",
    heroImageCredit: "Taha Samet Arslan",
    heroImageLink: "https://www.pexels.com/photo/delicious-dishes-with-wine-in-restaurant-7627408/",
    overviewTitle: "Digital experiences that help restaurants look polished and stay easier to book.",
    overviewParagraphs: [
      "Restaurant websites need to do a few things well: communicate the experience, show the menu clearly, and make reservations or inquiries easy. When those basics are unclear, the brand feels weaker than it should.",
      "We design restaurant-focused pages, promotional assets, and booking-friendly experiences that support real guest actions without adding unnecessary complexity."
    ],
    bestFor: "Restaurants, cafes, bars, bakeries, food concepts, and hospitality brands.",
    focus: "Menu clarity, reservations, local discovery, promotions, and visual brand presentation.",
    process: [
      {
        step: "Step 01",
        title: "Offer and audience review",
        description: "We identify the dining experience, the traffic sources that matter, and the actions guests need to take quickly."
      },
      {
        step: "Step 02",
        title: "Conversion-focused structure",
        description: "We organize menus, reservation prompts, promotions, and key business details so the site is easier to use."
      },
      {
        step: "Step 03",
        title: "Design and campaign support",
        description: "We shape the visual presentation and supporting assets so the brand feels consistent across web and marketing."
      },
      {
        step: "Step 04",
        title: "Operational polish",
        description: "We refine the flow for bookings, inquiries, and updates so your team spends less time fixing avoidable issues."
      }
    ],
    outcomes:
      "More credible presentation, clearer guest actions, smoother booking flow, and better support for promotions.",
    highlights: [
      "Menu and reservation pages",
      "Launch and seasonal campaign assets",
      "Location and opening-hours clarity",
      "Hospitality brand presentation systems"
    ]
  },
  {
    id: "healthcare",
    name: "Healthcare",
    path: "/industries/healthcare",
    number: "04",
    heroLabel: "Industry Solutions",
    heroTitle: "Healthcare websites and patient-facing systems built around clarity, reassurance, and structured access.",
    heroDescription:
      "We help healthcare providers create calmer digital experiences that explain services clearly and make appointments, inquiries, and trust-building easier.",
    heroImageUrl: "https://images.pexels.com/photos/7580254/pexels-photo-7580254.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    heroImageAlt: "Doctor with stethoscope consulting patient, holding medication in office setting.",
    heroImageCredit: "cottonbro studio",
    heroImageLink: "https://www.pexels.com/photo/doctor-sitting-by-table-with-patient-7580254/",
    overviewTitle: "Healthcare digital experiences designed for confidence, readability, and patient flow.",
    overviewParagraphs: [
      "Healthcare websites carry a heavier trust burden than most business sites. Patients need clarity fast, especially when they are making decisions under stress or uncertainty.",
      "We design healthcare-focused websites, appointment flows, and supporting materials that communicate professionalism, reduce confusion, and help patients move forward with more confidence."
    ],
    bestFor: "Clinics, practices, dental offices, specialists, wellness providers, and healthcare groups.",
    focus: "Patient trust, service explanation, appointment flow, readability, and structured navigation.",
    process: [
      {
        step: "Step 01",
        title: "Patient journey review",
        description: "We identify what patients need to know first, where confusion happens, and what actions should be most accessible."
      },
      {
        step: "Step 02",
        title: "Information design",
        description: "We structure services, practitioner information, and booking pathways so people can understand the offer more quickly."
      },
      {
        step: "Step 03",
        title: "Interface refinement",
        description: "We design a calmer, clearer visual system that supports trust and easier decision-making."
      },
      {
        step: "Step 04",
        title: "Operational integration",
        description: "We align contact points, forms, and appointment actions so the handoff to your team is smoother."
      }
    ],
    outcomes:
      "Stronger trust signals, clearer service communication, better patient navigation, and easier appointment handling.",
    highlights: [
      "Clinic and practice websites",
      "Appointment and inquiry forms",
      "Service explanation pages",
      "Healthcare campaign and educational assets"
    ]
  },
  {
    id: "e-commerce",
    name: "E-commerce",
    path: "/industries/e-commerce",
    number: "05",
    heroLabel: "Industry Solutions",
    heroTitle: "E-commerce pages and systems focused on product clarity, trust, and conversion flow.",
    heroDescription:
      "We help e-commerce brands improve product presentation, campaign landing pages, and supporting workflows so more visitors can move cleanly toward purchase.",
    heroImageUrl: "https://images.pexels.com/photos/6170188/pexels-photo-6170188.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    heroImageAlt: "Woman sorting parcels at her desk in a logistics office, demonstrating efficient workspace organization.",
    heroImageCredit: "Tima Miroshnichenko",
    heroImageLink: "https://www.pexels.com/photo/focused-woman-holding-a-parcel-6170188/",
    overviewTitle: "E-commerce experiences that reduce friction and present products with more confidence.",
    overviewParagraphs: [
      "E-commerce performance is shaped by more than ads and product quality. The on-site experience has to make products feel credible, easy to compare, and easy to buy.",
      "We support product-focused brands with landing pages, visual systems, storefront refinement, and campaign assets that help shoppers move with less hesitation."
    ],
    bestFor: "Direct-to-consumer brands, online stores, product launches, and multi-product e-commerce teams.",
    focus: "Product storytelling, campaign pages, conversion flow, trust-building, and storefront usability.",
    process: [
      {
        step: "Step 01",
        title: "Store and funnel review",
        description: "We assess the product journey, traffic intent, and the points where shoppers lose momentum."
      },
      {
        step: "Step 02",
        title: "Offer and page planning",
        description: "We organize product information, proof, and action paths so purchase decisions feel easier."
      },
      {
        step: "Step 03",
        title: "Design and campaign buildout",
        description: "We refine storefront and promotional assets around clarity, hierarchy, and stronger calls to action."
      },
      {
        step: "Step 04",
        title: "Iteration support",
        description: "We help shape reusable assets and page structures that are easier to extend across launches and campaigns."
      }
    ],
    outcomes:
      "Clearer product pages, stronger offer communication, cleaner campaign flow, and better conversion readiness.",
    highlights: [
      "Product landing pages",
      "Collection and campaign visuals",
      "Promo and launch assets",
      "Storefront UX refinement"
    ]
  },
  {
    id: "consultants",
    name: "Consultants",
    path: "/industries/consultants",
    number: "06",
    heroLabel: "Industry Solutions",
    heroTitle: "Consulting websites and positioning systems that make expertise easier to trust.",
    heroDescription:
      "We help consultants package their expertise online with clearer messaging, stronger authority signals, and inquiry flows that attract the right clients.",
    heroImageUrl: "https://images.pexels.com/photos/8204963/pexels-photo-8204963.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    heroImageAlt: "A professional consultation session with a couple and a social worker in a modern office environment.",
    heroImageCredit: "Pavel Danilyuk",
    heroImageLink: "https://www.pexels.com/photo/social-worker-checking-on-adoptive-parents-8204963/",
    overviewTitle: "Consultant websites built around positioning, trust, and qualified inquiries.",
    overviewParagraphs: [
      "Consulting businesses often sell expertise that is nuanced and hard to explain quickly. A good website has to simplify that value without flattening it.",
      "We help consultants shape clearer offers, cleaner page structure, and better lead capture so prospective clients understand the value faster and know when to reach out."
    ],
    bestFor: "Independent consultants, advisory firms, coaches, strategists, and specialist service providers.",
    focus: "Positioning, credibility, offer structure, thought-leadership support, and lead quality.",
    process: [
      {
        step: "Step 01",
        title: "Offer and audience clarity",
        description: "We identify the decision-makers you serve, the problems you solve, and the message that should lead the site."
      },
      {
        step: "Step 02",
        title: "Authority-focused structure",
        description: "We shape the page narrative around expertise, proof, outcomes, and a clean inquiry path."
      },
      {
        step: "Step 03",
        title: "Design and content direction",
        description: "We create a presentation style that feels credible, restrained, and aligned with your consulting model."
      },
      {
        step: "Step 04",
        title: "Lead and follow-up alignment",
        description: "We refine forms, booking points, and supporting assets so conversations start with better context."
      }
    ],
    outcomes:
      "Stronger positioning, better authority signals, clearer offers, and more qualified inbound conversations.",
    highlights: [
      "Consulting and advisory websites",
      "Offer architecture and service pages",
      "Lead capture and discovery call flows",
      "Thought-leadership and deck support"
    ]
  },
  {
    id: "hvac",
    name: "HVAC",
    path: "/industries/hvac",
    number: "07",
    heroLabel: "Industry Solutions",
    heroTitle: "HVAC websites and lead systems built for urgent service calls, maintenance bookings, and local trust.",
    heroDescription:
      "We help heating and cooling companies present services clearly, capture urgent inquiries faster, and support maintenance-driven repeat business with cleaner digital flows.",
    heroImageUrl: "https://images.pexels.com/photos/5691633/pexels-photo-5691633.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    heroImageAlt: "HVAC technician servicing an indoor climate system.",
    heroImageCredit: "cottonbro studio",
    heroImageLink: "https://www.pexels.com/photo/man-repairing-an-air-conditioner-5691633/",
    overviewTitle: "HVAC digital experiences designed for fast response, service clarity, and repeat bookings.",
    overviewParagraphs: [
      "HVAC customers often visit with urgency. They need to know whether you handle installs, repairs, maintenance, and emergency calls, and they need an easy way to act right away.",
      "We help HVAC businesses structure service pages, financing or maintenance-plan messaging, and booking touchpoints so inquiries come in with better context and less hesitation."
    ],
    bestFor: "Residential HVAC companies, commercial contractors, maintenance providers, and local heating and cooling teams.",
    focus: "Emergency response paths, maintenance booking, service clarity, and local lead capture.",
    process: [
      {
        step: "Step 01",
        title: "Service-call journey review",
        description: "We identify the urgent and non-urgent reasons customers contact you and how each path should be handled."
      },
      {
        step: "Step 02",
        title: "Service and area-page planning",
        description: "We organize repairs, installations, tune-ups, and service-area messaging so visitors can find the right path quickly."
      },
      {
        step: "Step 03",
        title: "Conversion-focused buildout",
        description: "We shape the design around quick calls, booking requests, trust signals, and maintenance-plan visibility."
      },
      {
        step: "Step 04",
        title: "Operational handoff",
        description: "We align the intake flow so your office or dispatch team gets cleaner inquiries with better details."
      }
    ],
    outcomes:
      "Faster service inquiries, clearer service presentation, stronger local trust, and cleaner intake workflows.",
    highlights: [
      "Repair and installation service pages",
      "Seasonal campaign landing pages",
      "Maintenance-plan and financing flows",
      "Area-specific lead capture pages"
    ]
  },
  {
    id: "pest-control",
    name: "Pest Control",
    path: "/industries/pest-control",
    number: "08",
    heroLabel: "Industry Solutions",
    heroTitle: "Pest control websites and service funnels designed for fast action, reassurance, and recurring bookings.",
    heroDescription:
      "We help pest control businesses explain services more clearly, build trust for urgent home concerns, and support one-time and ongoing treatment inquiries.",
    heroImageUrl: "https://images.pexels.com/photos/19789837/pexels-photo-19789837.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    heroImageAlt: "Pest control technician performing outdoor household treatment near a residential property.",
    heroImageCredit: "Garda Pest Control Indonesia",
    heroImageLink: "https://www.pexels.com/photo/pest-exterminator-fogging-the-yard-19789837/",
    overviewTitle: "Pest control websites built for urgency, reassurance, and repeat service opportunities.",
    overviewParagraphs: [
      "Pest control customers often search when they are anxious or dealing with an immediate problem. The website needs to calm concerns, explain the process, and make booking easy.",
      "We help pest control companies structure treatment pages, local service-area content, and recurring-service offers so visitors can act quickly and feel more confident doing it."
    ],
    bestFor: "Residential pest control companies, termite specialists, wildlife removal teams, and recurring treatment providers.",
    focus: "Service reassurance, urgent lead capture, recurring plans, and local service coverage.",
    process: [
      {
        step: "Step 01",
        title: "Customer concern mapping",
        description: "We identify the situations that drive inquiries and what reassures prospects enough to reach out."
      },
      {
        step: "Step 02",
        title: "Treatment and area-page planning",
        description: "We organize treatment types, pests covered, service areas, and follow-up expectations with better clarity."
      },
      {
        step: "Step 03",
        title: "Trust-focused interface refinement",
        description: "We design around trust cues, call requests, inspections, and recurring-service visibility."
      },
      {
        step: "Step 04",
        title: "Lead handling support",
        description: "We align forms and intake details so your team receives cleaner requests and can follow up faster."
      }
    ],
    outcomes:
      "More qualified inquiries, clearer service messaging, stronger reassurance, and better recurring-service support.",
    highlights: [
      "Treatment and inspection landing pages",
      "Recurring service membership flows",
      "Local service-area pages",
      "Trust-building FAQ and proof sections"
    ]
  },
  {
    id: "plumbing",
    name: "Plumbing",
    path: "/industries/plumbing",
    number: "09",
    heroLabel: "Industry Solutions",
    heroTitle: "Plumbing websites and booking flows built for fast contact, local trust, and service clarity.",
    heroDescription:
      "We help plumbing businesses present services clearly, support emergency and non-emergency requests, and make local inquiry flows easier to act on.",
    heroImageUrl: "https://images.pexels.com/photos/8486972/pexels-photo-8486972.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    heroImageAlt: "Plumber repairing pipes under a sink with tools nearby.",
    heroImageCredit: "Kindel Media",
    heroImageLink: "https://www.pexels.com/photo/man-fixing-pipes-8486972/",
    overviewTitle: "Plumbing digital experiences that help customers act quickly and understand services more easily.",
    overviewParagraphs: [
      "Plumbing visitors usually want answers quickly: what services you handle, what areas you cover, and how to contact you in urgent situations.",
      "We help plumbing companies build cleaner service pages, emergency contact paths, and local landing pages that support real-world lead generation without extra friction."
    ],
    bestFor: "Residential plumbers, commercial plumbing contractors, drain specialists, and emergency-response teams.",
    focus: "Emergency contact paths, service clarity, area coverage, and local conversion flow.",
    process: [
      {
        step: "Step 01",
        title: "Call-intent review",
        description: "We identify the urgent and routine reasons customers reach out and how the site should guide each one."
      },
      {
        step: "Step 02",
        title: "Service and location planning",
        description: "We organize service offerings, areas served, and the clearest next steps for homeowners or commercial clients."
      },
      {
        step: "Step 03",
        title: "Trust-focused buildout",
        description: "We design around quick access, proof, contact options, and the operational details customers care about most."
      },
      {
        step: "Step 04",
        title: "Lead-flow alignment",
        description: "We make the handoff from website to phone, form, or dispatch smoother so the team gets better context faster."
      }
    ],
    outcomes:
      "Faster customer action, stronger trust, better service visibility, and a more effective local lead flow.",
    highlights: [
      "Emergency and standard service pages",
      "Drain, leak, and install landing pages",
      "Local service-area SEO pages",
      "Call and request-a-quote flows"
    ]
  },
  {
    id: "landscaping",
    name: "Landscaping",
    path: "/industries/landscaping",
    number: "10",
    heroLabel: "Industry Solutions",
    heroTitle: "Landscaping websites and quote funnels that help outdoor service brands look premium and book more confidently.",
    heroDescription:
      "We help landscaping companies present their work more clearly, qualify project inquiries better, and create smoother quote-request experiences.",
    heroImageUrl: "https://images.pexels.com/photos/9029162/pexels-photo-9029162.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    heroImageAlt: "Landscaping worker maintaining a large lawn and outdoor green space.",
    heroImageCredit: "Makrufin Muhammad",
    heroImageLink: "https://www.pexels.com/photo/a-man-cutting-the-grass-using-a-lawn-mower-9029162/",
    overviewTitle: "Landscaping digital experiences built for visual credibility, quote requests, and service clarity.",
    overviewParagraphs: [
      "Landscaping businesses often need to sell both the quality of the finished work and the reliability of the service. That means the website has to carry more visual and trust weight.",
      "We help landscaping teams organize project galleries, service packages, seasonal offers, and quote-request flows so prospects can see the value faster and reach out with better intent."
    ],
    bestFor: "Landscape designers, lawn care businesses, hardscaping teams, and maintenance-focused outdoor service brands.",
    focus: "Portfolio presentation, quote requests, service packaging, and seasonal local lead generation.",
    process: [
      {
        step: "Step 01",
        title: "Service and project review",
        description: "We identify the types of jobs you want more of and the proof prospects need before contacting you."
      },
      {
        step: "Step 02",
        title: "Gallery and service planning",
        description: "We organize your services, before-and-after visuals, and quote pathways for clearer decision-making."
      },
      {
        step: "Step 03",
        title: "Visual and conversion refinement",
        description: "We shape a more premium presentation that supports trust, differentiation, and easier lead capture."
      },
      {
        step: "Step 04",
        title: "Inquiry flow alignment",
        description: "We make it easier for prospects to describe their project so your team can respond with better context."
      }
    ],
    outcomes:
      "Stronger visual credibility, better-qualified quote requests, clearer service packaging, and more confident inquiries.",
    highlights: [
      "Landscape and lawn care websites",
      "Project galleries and before-after sections",
      "Quote request and consultation forms",
      "Seasonal campaign landing pages"
    ]
  },
  {
    id: "roofing",
    name: "Roofing",
    path: "/industries/roofing",
    number: "11",
    heroLabel: "Industry Solutions",
    heroTitle: "Roofing websites and estimate flows designed for trust, urgency, and higher-intent local leads.",
    heroDescription:
      "We help roofing companies present services clearly, support inspection and estimate requests, and build stronger digital trust for major-ticket decisions.",
    heroImageUrl: "https://images.pexels.com/photos/37623616/pexels-photo-37623616.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    heroImageAlt: "Roofing workers actively repairing a residential roof.",
    heroImageCredit: "Bulat843 🌙",
    heroImageLink: "https://www.pexels.com/photo/two-workers-repairing-a-house-roof-under-a-cloudy-sky-37623616/",
    overviewTitle: "Roofing digital experiences focused on credibility, estimates, and local service visibility.",
    overviewParagraphs: [
      "Roofing decisions often involve higher risk, higher spend, and more comparison. Customers need to trust the business quickly before they request an inspection or estimate.",
      "We help roofing companies improve service pages, proof sections, project showcases, and local landing pages so the business feels more credible and easier to contact."
    ],
    bestFor: "Roofing contractors, inspection teams, repair specialists, and residential or commercial roofing companies.",
    focus: "Estimate requests, storm-damage messaging, service credibility, and local lead generation.",
    process: [
      {
        step: "Step 01",
        title: "Inspection and estimate review",
        description: "We identify what prospects need to trust first and how to reduce friction around estimate requests."
      },
      {
        step: "Step 02",
        title: "Service and proof planning",
        description: "We organize repair, replacement, and inspection services alongside project proof and service-area visibility."
      },
      {
        step: "Step 03",
        title: "Trust-centered page buildout",
        description: "We design around authority, local proof, financing or insurance messaging, and strong contact paths."
      },
      {
        step: "Step 04",
        title: "Lead-quality alignment",
        description: "We improve the intake flow so inspection requests come with better detail and less back-and-forth."
      }
    ],
    outcomes:
      "Higher-trust presentation, stronger estimate request flow, clearer service pages, and better local lead quality.",
    highlights: [
      "Roof repair and replacement pages",
      "Inspection and estimate request flows",
      "Storm-damage and insurance landing pages",
      "Area-specific roofing SEO pages"
    ]
  },
  {
    id: "movers",
    name: "Movers",
    path: "/industries/movers",
    number: "12",
    heroLabel: "Industry Solutions",
    heroTitle: "Moving company websites and quote systems built for urgency, trust, and smoother booking inquiries.",
    heroDescription:
      "We help moving companies present services clearly, support quote requests with better detail, and make the business feel more reliable before the first call.",
    heroImageUrl: "https://images.pexels.com/photos/4246120/pexels-photo-4246120.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    heroImageAlt: "Mover carrying cardboard boxes in a home relocation setting.",
    heroImageCredit: "Blue Bird",
    heroImageLink: "https://www.pexels.com/photo/person-holding-cardboard-boxes-4246120/",
    overviewTitle: "Moving company digital experiences focused on quote quality, service clarity, and reassurance.",
    overviewParagraphs: [
      "Moving prospects often compare several providers quickly. They want to know what you move, where you operate, and how easy it will be to get a reliable quote.",
      "We help movers structure service types, area coverage, packing support, and quote-request forms so prospects can take action faster and your team gets better inquiry details."
    ],
    bestFor: "Residential movers, office relocation teams, long-distance moving companies, and packing or storage providers.",
    focus: "Quote-request flow, relocation service clarity, local trust, and better-qualified leads.",
    process: [
      {
        step: "Step 01",
        title: "Booking-intent review",
        description: "We identify the key information prospects need and the details your team needs to quote accurately."
      },
      {
        step: "Step 02",
        title: "Service and route planning",
        description: "We organize local, long-distance, packing, and storage service pages with clearer next steps."
      },
      {
        step: "Step 03",
        title: "Trust and conversion refinement",
        description: "We design the experience around reliability, proof, quote flow, and easier comparison."
      },
      {
        step: "Step 04",
        title: "Lead-detail optimization",
        description: "We improve forms and contact options so quote requests arrive with more complete and usable information."
      }
    ],
    outcomes:
      "Better quote-request quality, stronger trust, clearer service presentation, and more usable moving leads.",
    highlights: [
      "Moving and relocation websites",
      "Packing and storage service pages",
      "Quote request and survey forms",
      "Local and long-distance landing pages"
    ]
  },
  {
    id: "education",
    name: "Education",
    path: "/industries/education",
    number: "13",
    heroLabel: "Industry Solutions",
    heroTitle: "Education pages and systems that make programs, enrollment, and communication easier to understand.",
    heroDescription:
      "We help education-focused teams present courses, programs, and institutional information more clearly while supporting inquiries, applications, and student communication.",
    heroImageUrl: "https://images.pexels.com/photos/9159042/pexels-photo-9159042.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    heroImageAlt: "Students learning in a classroom setting with a teacher assisting and laptops on desks, creating an interactive education environment.",
    heroImageCredit: "Mikhail Nilov",
    heroImageLink: "https://www.pexels.com/photo/a-man-in-blazer-looking-at-the-students-work-9159042/",
    overviewTitle: "Education experiences designed for clarity, trust, and smoother enrollment journeys.",
    overviewParagraphs: [
      "Education websites often need to speak to multiple audiences at once: students, parents, administrators, and partners. Without clear structure, those needs compete with each other.",
      "We help schools, training programs, and education teams organize information better so programs feel easier to understand and application paths are less confusing."
    ],
    bestFor: "Schools, training providers, academies, online programs, and education-focused organizations.",
    focus: "Program communication, enrollment flow, information hierarchy, and multi-audience clarity.",
    process: [
      {
        step: "Step 01",
        title: "Audience and program mapping",
        description: "We identify who needs what information first and where the current structure slows decisions down."
      },
      {
        step: "Step 02",
        title: "Navigation and content planning",
        description: "We organize programs, outcomes, and action points so users can move toward application or inquiry with less confusion."
      },
      {
        step: "Step 03",
        title: "Interface refinement",
        description: "We design a calmer, more legible experience that supports longer reading and easier comparison."
      },
      {
        step: "Step 04",
        title: "Communication support",
        description: "We align forms, content blocks, and internal workflows so follow-up becomes more structured."
      }
    ],
    outcomes:
      "Clearer program presentation, better enrollment support, stronger trust, and easier user navigation.",
    highlights: [
      "Institution and program websites",
      "Enrollment and inquiry flows",
      "Course and curriculum pages",
      "Student-facing information systems"
    ]
  },
  {
    id: "recruitment-firms",
    name: "Recruitment Firms",
    path: "/industries/recruitment-firms",
    number: "14",
    heroLabel: "Industry Solutions",
    heroTitle: "Websites and hiring workflows for recruitment firms and HR teams that need trust, speed, and better candidate flow.",
    heroDescription:
      "We help recruitment-focused businesses present roles, industries, and hiring services more clearly while supporting candidate applications, employer inquiries, and stronger brand credibility.",
    heroImageUrl: "https://images.pexels.com/photos/7644016/pexels-photo-7644016.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    heroImageAlt: "Recruitment team meeting with professionals discussing hiring strategy in an office.",
    heroImageCredit: "MART PRODUCTION",
    heroImageLink: "https://www.pexels.com/photo/man-talking-to-colleagues-on-the-meeting-7644016/",
    overviewTitle: "Recruitment and HR digital experiences designed for trust, role clarity, and smoother hiring journeys.",
    overviewParagraphs: [
      "Recruitment firms and HR teams need to serve two audiences well at the same time: employers looking for reliable hiring support and candidates looking for the right opportunity. If the experience is unclear, both sides lose confidence quickly.",
      "We help hiring-focused businesses build clearer service pages, job-listing structures, application flows, and employer inquiry paths so the brand feels more credible and easier to work with."
    ],
    bestFor: "Recruitment firms, executive search agencies, staffing companies, internal HR teams, and talent acquisition groups.",
    focus: "Employer trust, candidate application flow, role clarity, service positioning, and hiring process efficiency.",
    process: [
      {
        step: "Step 01",
        title: "Audience and hiring-flow review",
        description: "We identify the needs of employers, candidates, and internal stakeholders so the website supports each journey more clearly."
      },
      {
        step: "Step 02",
        title: "Service and role-page planning",
        description: "We organize hiring services, sectors covered, job listings, and inquiry paths for faster understanding and stronger intent."
      },
      {
        step: "Step 03",
        title: "Trust and usability refinement",
        description: "We design around authority, clarity, and easier action so both candidates and clients can move forward with more confidence."
      },
      {
        step: "Step 04",
        title: "Operational handoff",
        description: "We align forms, applications, and intake details so your team receives cleaner inquiries and better hiring context."
      }
    ],
    outcomes:
      "Stronger hiring-brand credibility, clearer service positioning, smoother application flow, and better-qualified employer inquiries.",
    highlights: [
      "Recruitment firm and staffing websites",
      "Job board and role-listing pages",
      "Candidate application and screening flows",
      "Employer inquiry and talent-request forms"
    ]
  }
];

export const industriesById = Object.fromEntries(industries.map((industry) => [industry.id, industry]));
